import { KeyboardArrowLeft, KeyboardArrowRight, KeyboardArrowUp } from '@mui/icons-material';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { ControlAreaButton } from '@src/components/common/ControlAreaButton';
import ProgressCircle from '@src/components/common/ProgressCircle';
import { COLORS } from '@src/constants/design-tokens/colors';
import type { PageInfo } from '@src/hooks/useControlAreaNavigation';
import type { ControlAreaStyles } from '@src/hooks/useControlAreaStyles';
import type { ReactElement, ReactNode } from 'react';

type DownloadButtonProps = {
  icon: ReactNode;
  onClick: () => void;
  ariaLabel: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  visible?: boolean;
};

type ProgressOptions = {
  containerSx?: SxProps<Theme>;
  textSx?: SxProps<Theme>;
  animate?: boolean;
  animationActive?: boolean;
};

type ControlAreaBaseProps = {
  pageInfo: PageInfo | null;
  containerStyles: ControlAreaStyles['containerStyles'];
  buttonStyles: ControlAreaStyles['buttonStyles'];
  pageLabel: string;
  pageTitle?: string;
  progress: number;
  progressLabel?: string;
  progressOptions?: ProgressOptions;
  isFocusMode: boolean;
  isScrolling: boolean;
  isInputActive: boolean;
  onPrev: () => void;
  onNext: () => void;
  onScrollTop?: () => void;
  showScrollTop?: boolean;
  downloadButton?: DownloadButtonProps;
};

const PRIMARY_MAIN = COLORS.primary.main;
const PRIMARY_LIGHT = COLORS.primary['300'];

const clampProgress = (value: number): number => {
  if (Number.isNaN(value)) {
    return 0;
  }
  return Math.min(100, Math.max(0, value));
};

export function ControlAreaBase({
  pageInfo,
  containerStyles,
  buttonStyles,
  pageLabel,
  pageTitle,
  progress,
  progressLabel,
  progressOptions,
  isFocusMode,
  isScrolling,
  isInputActive,
  onPrev,
  onNext,
  onScrollTop,
  showScrollTop = true,
  downloadButton,
}: ControlAreaBaseProps): ReactElement | null {
  if (!pageInfo || pageInfo.currentIndex === -1) {
    return null;
  }

  const roundedProgress = clampProgress(progress);
  const label = progressLabel ?? `${Math.round(roundedProgress)}%`;
  const animationBaseColor = isFocusMode ? PRIMARY_LIGHT : PRIMARY_MAIN;

  // ページ進捗率の計算
  const pageProgress =
    pageInfo.totalPages > 0 ? ((pageInfo.currentIndex + 1) / pageInfo.totalPages) * 100 : 0;

  const baseProgressContainerSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    px: 0.6,
    py: 0.4,
    flexShrink: 0,
  };

  const animationContainerSx: SxProps<Theme> =
    progressOptions?.animate && progressOptions.animationActive
      ? {
          animation: 'highlight 1.0s ease-in-out',
          '@keyframes highlight': {
            '0%': {
              backgroundColor: 'transparent',
              borderRadius: '8px',
            },
            '25%': {
              backgroundColor: alpha(animationBaseColor, 0.1),
              borderRadius: '8px',
            },
            '50%': {
              backgroundColor: alpha(animationBaseColor, 0.15),
              borderRadius: '8px',
            },
            '100%': {
              backgroundColor: 'transparent',
              borderRadius: '8px',
            },
          },
        }
      : {};

  const baseProgressTextSx: SxProps<Theme> = {
    ml: 0.3,
    fontWeight: 600,
    color: 'text.secondary',
    fontSize: '0.5rem',
  };

  const animationTextSx: SxProps<Theme> =
    progressOptions?.animate && progressOptions.animationActive
      ? {
          color: animationBaseColor,
          fontWeight: 700,
          textShadow: `0 0 6px ${alpha(animationBaseColor, 0.6)}`,
          animation: 'textHighlight 1.0s ease-in-out',
          '@keyframes textHighlight': {
            '0%': {
              textShadow: `0 0 2px ${alpha(animationBaseColor, 0.2)}`,
            },
            '50%': {
              textShadow: `0 0 8px ${alpha(animationBaseColor, 0.8)}`,
            },
            '100%': {
              textShadow: `0 0 2px ${alpha(animationBaseColor, 0.2)}`,
            },
          },
        }
      : {};

  const downloadVisible = downloadButton?.visible ?? Boolean(downloadButton);

  const mergedProgressContainerSx = [
    baseProgressContainerSx,
    progressOptions?.containerSx ?? {},
    animationContainerSx,
  ] as SxProps<Theme>;

  const mergedProgressTextSx = [
    baseProgressTextSx,
    progressOptions?.textSx ?? {},
    animationTextSx,
    {
      opacity: isScrolling || isInputActive ? 0.5 : 1,
      transition: 'all 0.2s ease',
    },
  ] as SxProps<Theme>;

  return (
    <Box
      role="toolbar"
      aria-label="入力ページコントロール"
      sx={{ ...containerStyles, overflow: 'hidden' }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 0.5, sm: 0.75 },
          flexWrap: { xs: 'wrap', lg: 'nowrap' },
          width: '100%',
        }}
      >
        <ControlAreaButton
          icon={<KeyboardArrowLeft fontSize="small" />}
          onClick={onPrev}
          disabled={pageInfo.isFirst}
          ariaLabel="前のページ"
          sx={buttonStyles.primary}
        />

        <Typography
          variant="body2"
          sx={{
            mx: isFocusMode ? 0.5 : 0.25,
            fontWeight: 600,
            color: 'text.primary',
            minWidth: { xs: '48px', sm: '72px' },
            maxWidth: { xs: '120px', sm: '140px' },
            textAlign: 'center',
            fontSize: { xs: '0.5rem', sm: '0.6rem' },
            opacity: isScrolling || isInputActive ? 0.5 : 1,
            transition: 'opacity 0.2s ease',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flexShrink: 1,
            flexGrow: 0,
          }}
          aria-live="polite"
          aria-label="現在のページ名"
          title={pageTitle ?? pageLabel}
        >
          {pageLabel}
        </Typography>

        <Box sx={mergedProgressContainerSx}>
          <ProgressCircle value={roundedProgress} size={20} thickness={2} showPercentage={false} />
          <Typography variant="caption" sx={mergedProgressTextSx}>
            {label}
          </Typography>
        </Box>

        <ControlAreaButton
          icon={<KeyboardArrowRight fontSize="small" />}
          onClick={onNext}
          disabled={pageInfo.isLast}
          ariaLabel="次のページ"
          sx={buttonStyles.primary}
        />

        {downloadButton && downloadVisible ? (
          <ControlAreaButton
            icon={downloadButton.icon}
            onClick={downloadButton.onClick}
            disabled={downloadButton.disabled}
            ariaLabel={downloadButton.ariaLabel}
            sx={downloadButton.sx ?? buttonStyles.download}
          />
        ) : null}

        {showScrollTop && onScrollTop ? (
          <ControlAreaButton
            icon={<KeyboardArrowUp fontSize="small" />}
            onClick={onScrollTop}
            ariaLabel="上部にスクロール"
            sx={buttonStyles.scroll}
          />
        ) : null}
      </Box>

      {/* 現在位置を示す進捗バー */}
      <LinearProgress
        variant="determinate"
        value={pageProgress}
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: 3,
          bgcolor: isFocusMode ? alpha(PRIMARY_LIGHT, 0.1) : alpha(PRIMARY_MAIN, 0.1),
          '& .MuiLinearProgress-bar': {
            bgcolor: isFocusMode ? PRIMARY_LIGHT : PRIMARY_MAIN,
            transition: 'transform 0.3s ease-out',
          },
        }}
      />
    </Box>
  );
}
